import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { addColorPalettes } from "../../../../redux/reducers/colorPalette";
import { getRandomBrightColor, getRandomId } from "../../../../utils/helpers";
import { colors } from "../../../../utils";
import Button from "../../../Button";
import Input from "../../../Input";
import TextView from "../../../TextView";
import CustomColorPicker from "../../../CustomColorPicker";
import { PALETTE_TYPES } from "./constants";

// Redux id for the single manual palette held in the "color" slot.
const MANUAL_PALETTE_ID = "manual-palette";
const MIN_COLORS = 1;
// Wheel palettes drive up to 20 segments; segment palettes are smaller but we
// keep a generous cap so a user is never blocked while hand-picking.
const MAX_COLORS = 20;

// On-brand deterministic starter set (brand ink + purples). The user replaces
// these with their exact colors; deterministic so re-opening is stable.
const BRAND_STARTER = ["#123051", "#9B5AF8", "#6F27D6"];

const HEX_RE = /^#[0-9A-F]{6}$/;
const SHORT_HEX_RE = /^#[0-9A-F]{3}$/;

/**
 * Normalize a raw hex string to `#RRGGBB` (uppercase), expanding shorthand.
 * Returns null when the input is not a valid 3- or 6-digit hex.
 */
export const normalizeHex = (raw) => {
  if (raw === undefined || raw === null) return null;
  let v = String(raw).trim().toUpperCase();
  if (!v.startsWith("#")) v = `#${v}`;
  if (SHORT_HEX_RE.test(v)) {
    v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  }
  return HEX_RE.test(v) ? v : null;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ColorRow = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr 36px;
  align-items: center;
  gap: 10px;
`;

const Swatch = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${colors.lightBorder};
  cursor: pointer;
  padding: 0;
  background-color: ${({ color }) => color};
`;

/**
 * Manual Brand Palette builder tab.
 *
 * Lets the user pick exact hex colors (hex input rows + Add color + the shared
 * SketchPicker via CustomColorPicker) and previews them live through the same
 * shared preview + Select/Save path the auto tabs use. It keeps a single palette
 * in the redux "color" slot (selected), so the modal's existing preview and
 * savePalette footer work unchanged — no new save/persist path is introduced.
 */
export default function ManualPalette({
  paletteType = PALETTE_TYPES.SEGMENT,
  handleSelect,
  showSelectButton,
}) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState(() =>
    BRAND_STARTER.map((hex) => ({ id: getRandomId(), hex, draft: hex }))
  );
  const [paletteName, setPaletteName] = useState("My palette");
  const [pickerRowId, setPickerRowId] = useState(null);

  // Mirror the current rows into the redux "color" slot as the selected palette
  // so the shared preview (SegmentPreview / WheelPreview) and the modal's
  // Select/Save footer operate on the manual colors with no extra wiring.
  useEffect(() => {
    const palette = {
      index: 0,
      paletteName: paletteName?.trim() || "My palette",
      colors: rows.map((r, i) => ({
        hex: r.hex,
        id: r.id,
        selected: false,
        locked: false,
        index: i,
      })),
      id: MANUAL_PALETTE_ID,
      selected: true,
    };
    dispatch(
      addColorPalettes({ paletteType, type: "color", paletteColors: [palette] })
    );
  }, [rows, paletteName, paletteType, dispatch]);

  const setRowHex = (id, hex) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, hex, draft: hex } : r))
    );

  const handleDraftChange = (id, value) => {
    const norm = normalizeHex(value);
    setRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, draft: value, hex: norm || r.hex } : r
      )
    );
  };

  // On blur, snap the visible text back to the last valid hex.
  const handleDraftBlur = (id) =>
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, draft: r.hex } : r))
    );

  const addColor = () =>
    setRows((prev) =>
      prev.length >= MAX_COLORS
        ? prev
        : [
            ...prev,
            (() => {
              const hex = normalizeHex(getRandomBrightColor()) || "#123051";
              return { id: getRandomId(), hex, draft: hex };
            })(),
          ]
    );

  const removeColor = (id) =>
    setRows((prev) =>
      prev.length <= MIN_COLORS ? prev : prev.filter((r) => r.id !== id)
    );

  const pickerRow = rows.find((r) => r.id === pickerRowId);

  return (
    <Wrapper>
      <div>
        <TextView variant="subtitle" style={{ fontWeight: 700 }}>
          Pick your exact colors
        </TextView>
        <TextView>
          Build a {paletteType} palette by choosing each color yourself.
        </TextView>
      </div>

      <Input
        label="Palette name"
        variant="secondary"
        value={paletteName}
        onChange={(e) => setPaletteName(e.target.value)}
        maxLength={60}
        containerStyle={{ marginBottom: 0 }}
      />

      <RowsWrapper>
        {rows.map((row) => (
          <ColorRow key={row.id}>
            <Swatch
              type="button"
              color={row.hex}
              aria-label={`Choose color ${row.hex}`}
              onClick={() => setPickerRowId(row.id)}
            />
            <Input
              variant="secondary"
              value={row.draft}
              onChange={(e) => handleDraftChange(row.id, e.target.value)}
              onBlur={() => handleDraftBlur(row.id)}
              maxLength={7}
              inputStyle={{ textTransform: "uppercase" }}
              containerStyle={{ marginBottom: 0 }}
            />
            <IconButton
              aria-label="Remove color"
              disabled={rows.length <= MIN_COLORS}
              onClick={() => removeColor(row.id)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </ColorRow>
        ))}
      </RowsWrapper>

      <Button
        variant="outlined"
        disabled={rows.length >= MAX_COLORS}
        onClick={addColor}
        style={{ width: 160, height: 36 }}
      >
        <AddIcon fontSize="small" style={{ marginRight: 6 }} />
        Add color
      </Button>

      {showSelectButton ? (
        <Button
          onClick={handleSelect}
          style={{ width: 200, height: 40, marginTop: 4 }}
        >
          Save palette
        </Button>
      ) : (
        <TextView style={{ color: colors.lightGrey }}>
          Your colors preview on the left. Click Select to save this palette.
        </TextView>
      )}

      {pickerRow && (
        <CustomColorPicker
          visible={!!pickerRow}
          color={pickerRow.hex}
          onColorChange={(hex) =>
            setRowHex(pickerRow.id, normalizeHex(hex) || pickerRow.hex)
          }
          onClose={() => setPickerRowId(null)}
          onConfirm={() => setPickerRowId(null)}
          customStyle={{
            position: "fixed",
            top: "50%",
            left: "50%",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
    </Wrapper>
  );
}
